<template>
  <!-- Teleport to body: the trigger card uses backdrop-filter (glass), which would
       otherwise make these fixed overlays anchor to the card instead of the viewport. -->
  <Teleport to="body">
    <!-- Backdrop (click to close) -->
    <div class="fixed inset-0 bg-black/60 z-[100]" @click="closeCompare(false)"></div>

    <!-- Modal -->
    <div
      class="fixed inset-0 z-[101] flex items-center justify-center p-4"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modal-cartLabel"
      @click.self="closeCompare(false)"
    >
      <div class="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div class="p-5 md:p-6 relative">
          <!-- Close button -->
          <button
            class="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-900"
            type="button"
            @click="closeCompare(false)"
          >
            <span class="text-2xl leading-none">&times;</span>
          </button>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <!-- Image gallery -->
            <div>
              <div class="bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  :src="getMainImg(images[activeImage])"
                  class="w-full h-auto max-h-[62vh] object-contain"
                  :alt="displayName"
                />
              </div>

              <!-- Thumbnails -->
              <div v-if="images.length > 1" class="mt-3 flex flex-wrap gap-2">
                <button
                  v-for="(img, i) in images"
                  :key="i"
                  type="button"
                  class="w-14 h-16 rounded-md overflow-hidden border-2 transition-colors bg-gray-50"
                  :class="i === activeImage ? 'border-primary' : 'border-gray-200 hover:border-gray-300'"
                  @click="activeImage = i"
                >
                  <img :src="getThumbImg(img)" class="w-full h-full object-contain" :alt="`${displayName} ${i + 1}`" />
                </button>
              </div>
            </div>

            <!-- Product info -->
            <div class="text-left rtl:text-right flex flex-col">
              <!-- Status badges -->
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <span
                  v-if="productData.statusPreorder"
                  class="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold bg-primary text-white uppercase tracking-wide"
                >
                  {{ $t('release_date') }}<template v-if="productData.releaseDate">: {{ formatDate(productData.releaseDate) }}</template>
                </span>
                <span
                  v-if="productData.isPromo"
                  class="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold bg-red-600 text-white uppercase tracking-wide"
                >
                  {{ $t('promo') }}
                </span>
              </div>

              <h2 class="text-2xl font-semibold text-gray-800 leading-snug">{{ displayName }}</h2>

              <h3 v-if="productData.brandText" class="text-base text-gray-600 mt-1">
                {{ $t('brand') }}: {{ productData.brandText }}
              </h3>

              <!-- Price -->
              <div class="flex items-baseline flex-wrap gap-3 mt-4">
                <span v-if="productData.price" class="text-3xl font-bold text-primary">
                  € {{ showDecimalValueInLocale(productData.price) }}
                </span>
                <span
                  v-if="productData.refCli"
                  class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-primary text-white"
                >
                  RefCli: {{ productData.refCli }}
                </span>
              </div>
              <p v-if="productData.dvdSuggestedPriceAmount" class="text-sm text-gray-600 mt-1">
                {{ $t('products.retail_price') }}: € {{ showDecimalValueInLocale(productData.dvdSuggestedPriceAmount) }}
              </p>

              <!-- Detail grid -->
              <dl class="grid grid-cols-2 gap-x-6 gap-y-3 mt-5 pt-5 border-t border-gray-200 text-sm">
                <div>
                  <dt class="text-xs uppercase tracking-wide text-gray-500">ID</dt>
                  <dd class="font-medium text-gray-900">{{ productData.Identifier }}</dd>
                </div>
                <div>
                  <dt class="text-xs uppercase tracking-wide text-gray-500">EAN</dt>
                  <dd class="font-medium text-gray-900">{{ productData.ean || '—' }}</dd>
                </div>
                <div v-if="!productData.statusPreorder">
                  <dt class="text-xs uppercase tracking-wide text-gray-500">{{ $t('stock') }}</dt>
                  <dd>
                    <span v-if="productData.stock >= 30" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#009d66] text-white">30+</span>
                    <span v-else-if="productData.stock > 0" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#009d66] text-white">{{ productData.stock }}</span>
                    <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-400 text-white">0</span>
                  </dd>
                </div>
                <div v-if="productData.vgMenuLanguages && productData.vgMenuLanguages.length">
                  <dt class="text-xs uppercase tracking-wide text-gray-500">Languages</dt>
                  <dd class="flex flex-wrap gap-1 mt-0.5">
                    <img
                      v-for="lng in productData.vgMenuLanguages"
                      :key="lng"
                      :src="getLangImgUrl(lng)"
                      :alt="lng"
                      class="w-5 h-auto rounded-sm"
                      onerror="this.style.display='none'"
                    />
                  </dd>
                </div>
              </dl>

              <!-- Actions -->
              <div class="mt-auto pt-6">
                <div v-if="isAuthenticated" class="flex flex-wrap items-center gap-3">
                  <div class="flex items-center">
                    <input
                      type="number"
                      v-model.number="qty"
                      min="1"
                      max="10000"
                      class="w-[64px] h-11 text-center border border-gray-300 rounded-lg focus:border-[#009d66] focus:ring-2 focus:ring-[#4CAF50]/30 outline-none"
                    />
                  </div>
                  <button
                    @click="addToCart(productData.Identifier)"
                    class="h-11 px-6 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    {{ $t('products.add_to_cart') }} <i class="ti-shopping-cart"></i>
                  </button>
                  <NuxtLinkLocale
                    :to="detailLink"
                    class="h-11 px-6 inline-flex items-center border border-primary text-primary font-medium rounded-lg hover:bg-primary hover:text-white transition-colors"
                  >
                    {{ $t('details') }}
                  </NuxtLinkLocale>
                </div>
                <NuxtLinkLocale
                  v-else
                  :to="detailLink"
                  class="h-11 px-6 inline-flex items-center border border-primary text-primary font-medium rounded-lg hover:bg-primary hover:text-white transition-colors"
                >
                  {{ $t('details') }}
                </NuxtLinkLocale>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup>
import { useCartStore } from "~/store/CartStore";
import { useUtilityStore } from "~/store/UtililityStore";
import { useFilterStore } from "~/store/FilterStore";
import { useI18n } from "vue-i18n";
const props = defineProps(["productData", "isAuthenticated"]);
const utilityStore = useUtilityStore();
const filterStore = useFilterStore();
const selectedLanguage = computed(() => utilityStore.selectedLanguage);

const { t } = useI18n();
const { showDecimalValueInLocale, productLinkTitleString } = useUtils();

const activeImage = ref(0);
const qty = ref(1);
const images = computed(() => props.productData?.images || []);

// Reset gallery/qty whenever a different product is opened.
watch(() => props.productData?.Identifier, () => {
  activeImage.value = 0;
  qty.value = 1;
});

const { getImgUrl: buildImgUrl } = useProductImage();
const getMainImg = (path) => buildImgUrl(path, { width: 600, height: 900, quality: 80 });
const getThumbImg = (path) => buildImgUrl(path, { width: 120, height: 180 });

const getLangImgUrl = (path) =>
  languageMap[path?.toLocaleLowerCase()] ||
  `https://unpkg.com/language-icons/icons/${path?.toLocaleLowerCase()}.svg`;

const displayName = computed(() => {
  // In the search mapper `name` is already a resolved string (as the card uses it);
  // fall back to per-language object shapes just in case.
  const n = props.productData?.name;
  if (typeof n === "string" && n) return n;
  return n?.[selectedLanguage.value] || n?.en_GB || n?.fr_BE || "";
});

// Build the same slugged URL the product grid uses so the link carries the name.
// `productLinkTitleString` is a computed ref that returns a slugify fn → call .value().
const detailLink = computed(() => ({
  path:
    "/products/" +
    props.productData?.Identifier +
    "/" +
    (productLinkTitleString.value?.(props.productData?.computed_attr) || ""),
}));

const dayjs = useDayjs();
const formatDate = (date) => (date ? dayjs(date).format("DD MMM YYYY") : "");

const closeCompare = () => {
  filterStore.setShowQuickView({ show: false, item: {} });
};

const addToCart = async (id) => {
  try {
    const { status } = await useCartStore().addToCart({
      sku: id,
      qty: qty.value || 1,
    });
    if (status) {
      useNuxtApp().$showToast({ msg: t("alerts.added_to_cart.desc"), type: "success" });
    } else {
      useNuxtApp().$showToast({ msg: t("alerts.failed_add_cart.desc"), type: "error" });
    }
  } catch (e) {
    useNuxtApp().$showToast({ msg: t("alerts.failed_add_cart.desc"), type: "error" });
  }
};
</script>
